import React, { useState, useEffect, useRef, useCallback } from "react";
import { AlertCircle, Loader, RefreshCw, Wand2 } from "lucide-react";
import { authFetch, apiUrl } from "../../utils/api";
import "./DashboardRenderer.css";

/**
 * Renders a custom dashboard.
 *
 * v2 ("Fable path", 2026-09-06): the saved document is a self-contained kit
 * page (React + Recharts + Optimus Kit + Fable's compiled JSX). It runs in a
 * SANDBOXED iframe — scripts execute, but the frame has no origin, no cookies,
 * no session. Data reaches it through a postMessage bridge: the frame asks for
 * an allow-listed API path, THIS component performs the request with the
 * user's session (authFetch) and posts the result back. Runtime errors inside
 * the frame surface here, loudly, with a "ask Fable to fix" hand-off.
 *
 * v1 (legacy HTML): rendered exactly as before via dangerouslySetInnerHTML.
 */
const V2_MARK = "<!--optimus-dashboard-v2";
const ALLOWED_PREFIXES = ["/api/data-proxy/", "/api/mcp/execute", "/api/mcp-user/execute", "/api/custom-dashboards/"];

export function isV2Document(html) {
  return typeof html === "string" && html.indexOf(V2_MARK) >= 0 && html.indexOf(V2_MARK) < 80;
}

/**
 * Stamp the dashboard id onto an /api/mcp/execute body.
 *
 * A team-shared dashboard is bound to the OWNER's MCP, so a teammate's fetch
 * has to tell the server WHICH dashboard it is executing for — that id is the
 * only thing that lets the server decide whether the owner's credentials may be
 * used (it re-checks sharing, membership and ownership itself; this is a hint,
 * never an authorization). Nothing credential-bearing crosses into the frame,
 * and the frame never has to know any of this — which is why saved dashboard
 * documents need no regeneration.
 *
 * Defensive by design: a non-JSON, malformed, or non-object body is forwarded
 * exactly as it came, and a body that already carries a dashboardId is left
 * alone. This function never throws.
 */
export function withDashboardId(rawBody, dashboardId) {
  if (!dashboardId || typeof rawBody !== "string") return rawBody;
  try {
    const parsed = JSON.parse(rawBody);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return rawBody;
    if (parsed.dashboardId) return rawBody;
    return JSON.stringify({ ...parsed, dashboardId });
  } catch (_) {
    return rawBody;
  }
}

const DashboardRenderer = ({ dashboard, userId, onAskFable, readOnly = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardHtml, setDashboardHtml] = useState("");
  const [frameHeight, setFrameHeight] = useState(720);
  const [frameError, setFrameError] = useState(null);
  const [frameReady, setFrameReady] = useState(false);
  const iframeRef = useRef(null);
  const v2 = isV2Document(dashboard?.htmlCode);

  useEffect(() => {
    if (dashboard?.htmlCode) {
      setDashboardHtml(dashboard.htmlCode);
      setFrameError(null);
      setFrameReady(false);
      setLoading(false);
    } else {
      setError("No dashboard content available");
      setLoading(false);
    }
  }, [dashboard?.id, dashboard?.htmlCode, userId]);

  // ---- postMessage bridge (v2) ----
  const onMessage = useCallback(
    async (ev) => {
      const frame = iframeRef.current;
      if (!frame || ev.source !== frame.contentWindow) return; // only OUR frame
      const m = ev.data || {};
      if (m.type === "optimus:fetch") {
        const path = String(m.path || "");
        const allowed = ALLOWED_PREFIXES.some((p) => path.startsWith(p)) && !path.includes("..") && !/^\/api\/custom-dashboards\/[^/]+\/(refine|refresh)/.test(path);
        const method = String((m.init && m.init.method) || "GET").toUpperCase();
        if (!allowed || !["GET", "POST"].includes(method)) {
          frame.contentWindow.postMessage({ type: "optimus:fetch:result", id: m.id, ok: false, status: 403, error: `Blocked: ${method} ${path} is not an allowed dashboard data path`, path }, "*");
          return;
        }
        let body = method === "POST" ? m.init.body : undefined;
        if (method === "POST" && path.startsWith("/api/mcp/execute")) {
          body = withDashboardId(body, dashboard?.id);
        }
        try {
          const res = await authFetch(apiUrl(path), {
            method,
            headers: method === "POST" ? { "Content-Type": "application/json" } : undefined,
            body,
          });
          const text = await res.text();
          let data = text;
          try { data = JSON.parse(text); } catch (_) { /* non-JSON */ }
          const errMsg = !res.ok ? (data && (data.error || data.message)) || `HTTP ${res.status}` : undefined;
          frame.contentWindow.postMessage({ type: "optimus:fetch:result", id: m.id, ok: res.ok, status: res.status, data, error: errMsg, path }, "*");
        } catch (e) {
          frame.contentWindow.postMessage({ type: "optimus:fetch:result", id: m.id, ok: false, status: 0, error: e.message, path }, "*");
        }
      } else if (m.type === "optimus:resize") {
        const h = Number(m.height);
        if (h > 200 && h < 20000) setFrameHeight(Math.ceil(h) + 8);
      } else if (m.type === "optimus:error") {
        console.error("[Dashboard frame]", m.where, m.message);
        setFrameError({ where: m.where, message: m.message, status: m.status, path: m.path });
      } else if (m.type === "optimus:ready") {
        setFrameReady(true);
      }
    },
    [dashboard?.id],
  );

  useEffect(() => {
    if (!v2) return undefined;
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [v2, onMessage]);

  // Keep the frame's theme in step with the app
  useEffect(() => {
    if (!v2) return undefined;
    const send = () => {
      const t = document.documentElement.getAttribute("data-theme") || "dark";
      iframeRef.current?.contentWindow?.postMessage({ type: "optimus:theme", theme: t === "light" ? "light" : "dark" }, "*");
    };
    send();
    const obs = new MutationObserver(send);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, [v2, dashboardHtml, frameReady]);

  const reloadFrame = () => {
    setFrameError(null);
    setFrameReady(false);
    const html = dashboardHtml;
    setDashboardHtml("");
    setTimeout(() => setDashboardHtml(html), 30);
  };

  if (loading) {
    return (
      <div className="dashboard-renderer">
        <div className="renderer-loading">
          <Loader size={48} className="spin" />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="dashboard-renderer">
        <div className="renderer-error">
          <AlertCircle size={48} />
          <h3>Dashboard Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (v2) {
    return (
      <div className="dashboard-renderer dashboard-renderer-v2">
        {frameError && (
          <div className="dashboard-frame-error" role="alert">
            <AlertCircle size={16} />
            <span>
              This dashboard hit a problem{frameError.where ? ` (${frameError.where})` : ""}: {String(frameError.message).replace(/\{[\s\S]*$/, "").replace(/Error:\s*/g, "").trim().slice(0, 180) || "see the card for details"}
            </span>
            {/* Fix/reload affordances are owner-only: the server blocks refine
                for team viewers anyway — never render buttons that can't work. */}
            {!readOnly && (
              <button className="frame-error-btn" onClick={reloadFrame} title="Reload the dashboard">
                <RefreshCw size={14} /> Reload
              </button>
            )}
            {!readOnly && onAskFable && (
              <button className="frame-error-btn primary" onClick={() => onAskFable(`Fix this error: ${String(frameError.message).slice(0, 300)}`)} title="Send this error to Fable to fix">
                <Wand2 size={14} /> Ask Fable to fix
              </button>
            )}
          </div>
        )}
        {dashboardHtml && (
          <iframe
            // key per dashboard+version: React must MOUNT A NEW FRAME when the
            // active dashboard changes. Reusing the element briefly showed the
            // previous dashboard's document (and, for a legacy doc, ran its
            // inline scripts inside the sandbox) until state caught up — seen
            // right after the wizard closed (playtest 2026-09-06).
            key={`${dashboard?.id || "dash"}:${dashboard?.version || 0}`}
            ref={iframeRef}
            title={dashboard?.name || "Dashboard"}
            className="dashboard-frame"
            sandbox="allow-scripts allow-popups"
            srcDoc={dashboard?.htmlCode || dashboardHtml}
            style={{ height: frameHeight }}
            onLoad={() => {
              const t = document.documentElement.getAttribute("data-theme") || "dark";
              iframeRef.current?.contentWindow?.postMessage({ type: "optimus:theme", theme: t === "light" ? "light" : "dark" }, "*");
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="dashboard-renderer">
      <div className="dashboard-content-direct" dangerouslySetInnerHTML={{ __html: dashboardHtml }} />
    </div>
  );
};

export default DashboardRenderer;
